import { useMemo, useState, useRef } from "react";
import { OrphanMXRecord } from "./types";
import { formatCents } from "./helpers";

interface Props {
  open: boolean;
  onClose: () => void;
  orphanMX: OrphanMXRecord[];
  eventId: number;
  eventName: string;
  /** Display name of the processor behind this recon: "Stripe", "MX", or "Processor". */
  processorLabel?: string;
}

/* ------------------------------------------------------------------ */
/* Memo parser — extracts order fields from MX CSV memo JSON          */
/* ------------------------------------------------------------------ */

interface MemoOrderData {
  reference_id?: string;
  location_id?: number;
  user_id?: number;
  event_id?: number;
  subtotal?: number;
  tax?: number;
  tip?: number;
  digital_surcharge?: number;
  items?: any;
  discount?: any;
  device_app_id?: string;
  menu_id?: number;
  vendor_id?: number;
}

function parseMemoOrder(memo: string | null | undefined): MemoOrderData | null {
  if (!memo) return null;
  try {
    const parsed = JSON.parse(memo);
    // Format 1: { data: { localOrder: { ... } } }
    if (parsed?.data?.localOrder) return parsed.data.localOrder;
    // Format 2: { order: { ... } }
    if (parsed?.order) return parsed.order;
    return null;
  } catch {
    // Not valid JSON — try regex extraction for key fields
    const extract = (field: string): string | null => {
      const m = memo!.match(new RegExp(`"${field}"\\s*:\\s*([\\d]+|"[^"]*")`));
      if (!m) return null;
      return m[1].replace(/"/g, "");
    };
    const locStr = extract("location_id");
    const userStr = extract("user_id");
    if (!locStr && !userStr) return null;
    return {
      location_id: locStr ? parseInt(locStr, 10) : undefined,
      user_id: userStr ? parseInt(userStr, 10) : undefined,
    };
  }
}

/* ------------------------------------------------------------------ */
/* SQL helpers                                                        */
/* ------------------------------------------------------------------ */

function pgEscape(value: string | null | undefined): string {
  if (value == null) return "NULL";
  return `'${String(value).replace(/'/g, "''")}'`;
}

function pgTimestamp(dateStr: string | null | undefined): string {
  if (!dateStr) return "NOW()";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "NOW()";
    return `'${d.toISOString()}'::timestamptz`;
  } catch {
    return "NOW()";
  }
}

function buildPaymentData(rec: OrphanMXRecord): string {
  const obj: Record<string, any> = {};
  if (rec.mx_last4) {
    obj.cardAccount = { last4: rec.mx_last4 };
  }
  if (rec.mx_card_type) {
    obj.cardType = rec.mx_card_type;
  }
  obj._source = "mx-reconciliation-insert";
  return pgEscape(JSON.stringify(obj));
}

/* ------------------------------------------------------------------ */
/* SQL generators                                                     */
/* ------------------------------------------------------------------ */

function generateHasuraSQL(
  records: OrphanMXRecord[],
  eventId: number,
  proc: string,
): string {
  if (records.length === 0) return `-- No orphan ${proc} records to insert.`;

  const lines: string[] = [];
  lines.push("-- ============================================================");
  lines.push(`-- ${proc} Reconciliation: Insert Orphan ${proc} Records into Ronin`);
  lines.push("-- Hasura SQL Runner compatible (one statement per record)");
  lines.push(`-- Event ID: ${eventId}`);
  lines.push(`-- Generated: ${new Date().toISOString()}`);
  lines.push(`-- Records: ${records.length}`);
  lines.push("-- ============================================================");
  lines.push("");
  lines.push("-- Run each block separately in Hasura Console → Data → SQL.");
  lines.push(`-- All fields auto-extracted from ${proc} memo. Records without memo data are excluded.`);
  lines.push("");

  for (let i = 0; i < records.length; i++) {
    const rec = records[i];
    const memo = parseMemoOrder(rec.memo);
    const amountCents = rec.mx_amount_cents;
    const ts = pgTimestamp(rec.mx_date);
    const refId = pgEscape(rec.reference_id);
    const paymentData = buildPaymentData(rec);

    const locId = memo!.location_id!;
    const uId = memo?.user_id ?? 0;
    const subtotal = memo?.subtotal ?? amountCents;
    const tax = memo?.tax ?? 0;
    const tip = memo?.tip ?? 0;
    const surcharge = memo?.digital_surcharge ?? 0;
    const items = memo?.items ? pgEscape(JSON.stringify(memo.items)) : "NULL";
    const discount = memo?.discount ? pgEscape(JSON.stringify(memo.discount)) : "NULL";
    const deviceAppId = memo?.device_app_id ? pgEscape(memo.device_app_id) : "NULL";
    const menuId = memo?.menu_id ? String(memo.menu_id) : "NULL";
    const vendorId = memo?.vendor_id ? String(memo.vendor_id) : "NULL";

    lines.push(`-- Record ${i + 1}/${records.length}: ref=${rec.reference_id}, amount=${formatCents(amountCents)}, location=${locId}`);
    lines.push(`WITH new_order AS (`);
    lines.push(`  INSERT INTO orders (`);
    lines.push(`    reference_id, status, subtotal, tax, tip, digital_surcharge,`);
    lines.push(`    credit_present_charged, credit_not_present_charged,`);
    lines.push(`    items, discount, device_app_id, menu_id, vendor_id,`);
    lines.push(`    transaction_at, transaction_time,`);
    lines.push(`    event_id, location_id, user_id`);
    lines.push(`  ) VALUES (`);
    lines.push(`    ${refId}, 'processed', ${subtotal}, ${tax}, ${tip}, ${surcharge},`);
    lines.push(`    ${amountCents}, 0,`);
    lines.push(`    ${items}, ${discount}, ${deviceAppId}, ${menuId}, ${vendorId},`);
    lines.push(`    ${ts}, ${ts},`);
    lines.push(`    ${eventId}, ${locId}, ${uId}`);
    lines.push(`  ) RETURNING id AS order_id`);
    lines.push(`),`);
    lines.push(`new_payment AS (`);
    lines.push(`  INSERT INTO payments (`);
    lines.push(`    reference_id, payment_type, amount, status, payment_data`);
    lines.push(`  ) VALUES (`);
    lines.push(`    ${refId}, 'credit', ${amountCents}, 'approved', ${paymentData}`);
    lines.push(`  ) RETURNING id AS payment_id`);
    lines.push(`)`);
    lines.push(`INSERT INTO transactions (order_id, payment_id, amount, status)`);
    lines.push(`SELECT new_order.order_id, new_payment.payment_id, ${amountCents}, 'approved'`);
    lines.push(`FROM new_order, new_payment;`);
    lines.push("");
  }

  return lines.join("\n");
}

function generateRawSQL(
  records: OrphanMXRecord[],
  eventId: number,
  proc: string,
): string {
  if (records.length === 0) return `-- No orphan ${proc} records to insert.`;

  const lines: string[] = [];
  lines.push("-- ============================================================");
  lines.push(`-- ${proc} Reconciliation: Insert Orphan ${proc} Records into Ronin`);
  lines.push(`-- Event ID: ${eventId}`);
  lines.push(`-- Generated: ${new Date().toISOString()}`);
  lines.push(`-- Records: ${records.length}`);
  lines.push("-- ============================================================");
  lines.push("");
  lines.push("-- Run inside a transaction so you can ROLLBACK if something looks wrong.");
  lines.push(`-- All fields auto-extracted from ${proc} memo. Records without memo data are excluded.`);
  lines.push("");
  lines.push("BEGIN;");
  lines.push("");

  for (let i = 0; i < records.length; i++) {
    const rec = records[i];
    const memo = parseMemoOrder(rec.memo);
    const amountCents = rec.mx_amount_cents;
    const ts = pgTimestamp(rec.mx_date);
    const refId = pgEscape(rec.reference_id);
    const paymentData = buildPaymentData(rec);

    const locId = memo!.location_id!;
    const uId = memo?.user_id ?? 0;
    const subtotal = memo?.subtotal ?? amountCents;
    const tax = memo?.tax ?? 0;
    const tip = memo?.tip ?? 0;
    const surcharge = memo?.digital_surcharge ?? 0;

    lines.push(`-- Record ${i + 1}/${records.length}: ref=${rec.reference_id}, amount=${formatCents(amountCents)}, location=${locId}`);
    lines.push(`WITH new_order AS (`);
    lines.push(`  INSERT INTO orders (`);
    lines.push(`    reference_id, status, subtotal, tax, tip, digital_surcharge,`);
    lines.push(`    credit_present_charged, credit_not_present_charged,`);
    lines.push(`    transaction_at, transaction_time,`);
    lines.push(`    event_id, location_id, user_id`);
    lines.push(`  ) VALUES (`);
    lines.push(`    ${refId}, 'processed', ${subtotal}, ${tax}, ${tip}, ${surcharge},`);
    lines.push(`    ${amountCents}, 0,`);
    lines.push(`    ${ts}, ${ts},`);
    lines.push(`    ${eventId}, ${locId}, ${uId}`);
    lines.push(`  ) RETURNING id AS order_id`);
    lines.push(`),`);
    lines.push(`new_payment AS (`);
    lines.push(`  INSERT INTO payments (`);
    lines.push(`    reference_id, payment_type, amount, status, payment_data`);
    lines.push(`  ) VALUES (`);
    lines.push(`    ${refId}, 'credit', ${amountCents}, 'approved', ${paymentData}`);
    lines.push(`  ) RETURNING id AS payment_id`);
    lines.push(`)`);
    lines.push(`INSERT INTO transactions (order_id, payment_id, amount, status)`);
    lines.push(`SELECT new_order.order_id, new_payment.payment_id, ${amountCents}, 'approved'`);
    lines.push(`FROM new_order, new_payment;`);
    lines.push("");
  }

  lines.push("-- Review the results before committing:");
  lines.push(`-- SELECT * FROM orders WHERE event_id = ${eventId} ORDER BY id DESC LIMIT ${records.length};`);
  lines.push("");
  lines.push("-- If everything looks good:");
  lines.push("COMMIT;");
  lines.push("");
  lines.push("-- If something is wrong:");
  lines.push("-- ROLLBACK;");

  return lines.join("\n");
}

/* ------------------------------------------------------------------ */
/* Modal component                                                    */
/* ------------------------------------------------------------------ */

export function GenerateSQLModal({ open, onClose, orphanMX, eventId, eventName, processorLabel = "MX" }: Props) {
  const [format, setFormat] = useState<"hasura" | "raw">("hasura");
  const [copied, setCopied] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // Only include approved/settled orphans that have memo data we can extract from.
  // Records with empty/unparseable memos are skipped — those will be handled manually.
  const approvedOrphans = useMemo(() => {
    return orphanMX.filter((r) => {
      const s = (r.mx_status || "").toLowerCase();
      if (!s.includes("approved") && !s.includes("settled")) return false;
      const memo = parseMemoOrder(r.memo);
      return memo != null && memo.location_id != null;
    });
  }, [orphanMX]);

  // Count of approved orphans skipped due to missing memo
  const skippedCount = useMemo(() => {
    return orphanMX.filter((r) => {
      const s = (r.mx_status || "").toLowerCase();
      if (!s.includes("approved") && !s.includes("settled")) return false;
      const memo = parseMemoOrder(r.memo);
      return memo == null || memo.location_id == null;
    }).length;
  }, [orphanMX]);

  const sql = useMemo(() => {
    if (format === "hasura") {
      return generateHasuraSQL(approvedOrphans, eventId, processorLabel);
    }
    return generateRawSQL(approvedOrphans, eventId, processorLabel);
  }, [approvedOrphans, eventId, format, processorLabel]);

  const handleCopy = () => {
    if (textRef.current) {
      textRef.current.select();
      navigator.clipboard.writeText(sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!open) return null;

  const totalCents = approvedOrphans.reduce((s, r) => s + r.mx_amount_cents, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Generate SQL — Insert Orphan {processorLabel} Records</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {approvedOrphans.length} record{approvedOrphans.length !== 1 ? "s" : ""} with memo data · {formatCents(totalCents)} total · Event: {eventName}
              {skippedCount > 0 && (
                <span className="text-orange-500 ml-1">
                  · {skippedCount} skipped (no memo)
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Config bar */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Format</label>
              <div className="flex gap-1 rounded-lg bg-gray-100 p-0.5">
                <button
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    format === "hasura" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setFormat("hasura")}
                >
                  Hasura SQL
                </button>
                <button
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    format === "raw" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setFormat("raw")}
                >
                  Raw SQL
                </button>
              </div>
            </div>
            <div className="flex-1" />
            <button
              onClick={handleCopy}
              className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy SQL
                </>
              )}
            </button>
          </div>
        </div>

        {/* Warnings */}
        {approvedOrphans.length === 0 && (
          <div className="px-6 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500">
            {skippedCount > 0
              ? `All ${skippedCount} approved orphan records have empty memos and must be handled manually.`
              : `No approved/settled orphan ${processorLabel} records to insert.`}
          </div>
        )}

        {/* SQL output */}
        <div className="flex-1 overflow-auto p-4">
          <textarea
            ref={textRef}
            readOnly
            value={sql}
            className="w-full h-full min-h-[300px] font-mono text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4 resize-none focus:outline-none"
            spellCheck={false}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg text-xs text-gray-500">
          {format === "hasura"
            ? "Each record is a single CTE statement. Paste into Hasura Console → Data → SQL."
            : "Wrap in a transaction. Use @order_id_N / @payment_id_N variables from RETURNING clauses."}
        </div>
      </div>
    </div>
  );
}
